import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  insureds,
  integrationCalls,
  type InsuredDetail,
  type IntegrationCall,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatCpfCnpj, formatDateTime } from '@/shared/lib/format';
import { nomeDoProvedor } from '@/shared/lib/nomeDoProvedor';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { DataTable } from '@/shared/ui/DataTable';
import { DetailCard } from '@/shared/ui/DetailLayout';
import { EmptyState } from '@/shared/ui/EmptyState';
import { HistoryTable } from '@/shared/ui/HistoryTab';
import { Pagination } from '@/shared/ui/Pagination';

import { CallDetailModal, CallStatusBadge } from '../integrations/IntegrationCallParts';

import { rotuloDaAcao, rotuloDoCampo } from './rotulos';

const tamanhoDaPagina = 25;

/**
 * Aba "Historico de Alteracoes" do segurado (P-SEG-2), pela rota propria
 * `GET /api/v1/insureds/{id}/change-history`: quem alterou, quando, o evento e o
 * "Visualizar alteracoes" com o antes e o depois de cada campo.
 *
 * A origem (IP e navegador de quem alterou) so vem para quem le a trilha de
 * auditoria da empresa; para os demais a coluna diz "nao exibida".
 */
export function InsuredChangeHistoryTab({ segurado }: { segurado: InsuredDetail }) {
  const [offset, setOffset] = useState(0);
  const consulta = useQuery({
    queryKey: ['insured', segurado.id, 'change-history', offset],
    queryFn: ({ signal }) =>
      insureds.changeHistory(segurado.id, { limit: tamanhoDaPagina, offset }, signal),
  });

  return (
    <DetailCard
      title="Historico de Alteracoes"
      description="Toda mudanca no cadastro do segurado, inclusive enderecos, telefones, CNAEs, nomes fantasia e o quadro societario, com o antes e o depois."
      loading={consulta.isPending}
      error={consulta.error}
    >
      <div className="flex flex-col">
        <HistoryTable
          rows={consulta.data?.items ?? []}
          emptyText="Nenhuma alteracao registrada"
          showOrigin
          actionLabel={rotuloDaAcao}
          fieldLabel={rotuloDoCampo}
        />
        {consulta.data ? (
          <Pagination
            total={consulta.data.total}
            limit={tamanhoDaPagina}
            offset={offset}
            onChange={setOffset}
          />
        ) : null}
      </div>
    </DetailCard>
  );
}

/**
 * Aba "Historico de Consultas" do segurado (P-SEG-2): as chamadas a sistemas
 * externos feitas sobre o segurado, pelo registro de integracoes (objeto
 * `segurado`). As consultas a bureau e as outras integracoes tem leituras separadas,
 * como na tela de consultas a bureau e na aba Registro de /integracoes.
 *
 * Nesta versao nenhuma consulta e feita sobre o segurado: a consulta cadastral e a de
 * PEP do segurado dependem de regra pendente (SEG-001, SEG-002) e da decisao sobre o
 * bureau (D-01). A aba ja le o registro, e mostra o que vier.
 */
export function InsuredConsultationsTab({ segurado }: { segurado: InsuredDetail }) {
  const { can } = useAuth();
  const podeBureau = can('integracoes.erro_consulta.view');
  const podeRegistro = can('integracoes.integracao.view');
  const [aberta, setAberta] = useState<string | null>(null);

  const filtro = {
    subject_type: 'segurado' as const,
    subject_id: segurado.id,
    limit: 50,
  };
  const bureau = useQuery({
    queryKey: ['integration-calls', 'segurado', segurado.id, 'bureau'],
    queryFn: ({ signal }) =>
      integrationCalls.list({ ...filtro, kind: 'bureau' }, signal),
    enabled: podeBureau,
  });
  const registro = useQuery({
    queryKey: ['integration-calls', 'segurado', segurado.id, 'registro'],
    queryFn: ({ signal }) => integrationCalls.list(filtro, signal),
    enabled: podeRegistro,
  });

  if (!podeBureau && !podeRegistro) {
    return (
      <DetailCard title="Historico de Consultas">
        <EmptyState
          title="Consulta restrita"
          description="O historico de consultas a sistemas externos exige a leitura das consultas a bureau ou do registro de integracoes."
        />
      </DetailCard>
    );
  }

  // As duas leituras juntas, da mais recente para a mais antiga.
  const linhas: IntegrationCall[] = [
    ...(bureau.data?.items ?? []),
    ...(registro.data?.items ?? []),
  ].sort((a, b) => b.started_at.localeCompare(a.started_at));
  const carregando =
    (podeBureau && bureau.isPending) || (podeRegistro && registro.isPending);

  return (
    <DetailCard
      title="Historico de Consultas"
      description="Chamadas a sistemas externos feitas sobre este segurado, como consultas cadastrais e de PEP."
      loading={carregando}
      error={bureau.error ?? registro.error}
    >
      <div className="flex flex-col gap-3">
        {!podeBureau || !podeRegistro ? (
          <Alert tone="info" title="Parte do historico nao aparece">
            {podeBureau
              ? 'As outras integracoes exigem a leitura do registro de integracoes.'
              : 'As consultas a bureau exigem a leitura das consultas a bureau.'}
          </Alert>
        ) : null}
        <DataTable
          columns={[
            {
              key: 'quando',
              header: 'Data',
              render: (linha: IntegrationCall) => formatDateTime(linha.started_at),
            },
            {
              key: 'consulta',
              header: 'Consulta',
              render: (linha: IntegrationCall) =>
                linha.call_type_name || linha.call_type,
            },
            {
              key: 'provedor',
              header: 'Provedor',
              hideBelow: 'sm',
              render: (linha: IntegrationCall) =>
                nomeDoProvedor(linha.provider_key, linha.provider_name),
            },
            {
              key: 'documento',
              header: 'Documento',
              hideBelow: 'md',
              render: (linha: IntegrationCall) =>
                linha.document_masked ? linha.document : formatCpfCnpj(linha.document),
            },
            {
              key: 'situacao',
              header: 'Situacao',
              render: (linha: IntegrationCall) => <CallStatusBadge call={linha} />,
            },
            {
              key: 'quem',
              header: 'Pedida por',
              hideBelow: 'md',
              render: (linha: IntegrationCall) => linha.requested_label || 'sistema',
            },
            {
              key: 'acoes',
              header: 'Acoes',
              render: (linha: IntegrationCall) => (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setAberta(linha.id)}
                >
                  Ver detalhes
                </Button>
              ),
            },
          ]}
          items={linhas}
          keyOf={(linha) => linha.id}
          empty={<EmptyState title="Nenhuma consulta registrada para o segurado" />}
        />
      </div>
      {aberta ? (
        <CallDetailModal callId={aberta} onClose={() => setAberta(null)} />
      ) : null}
    </DetailCard>
  );
}
